import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import type { LicenseSkillAssignment } from '@/types/domain';

interface SkillSelectionPanelProps {
  title: string;
  description: string;
  skills: LicenseSkillAssignment[];
  selectedSkillIds: string[];
  loading: boolean;
  onToggleSkill: (skillId: string, checked: boolean) => void;
}

/**
 * Reusable skills selector used by create, batch-create and edit dialogs.
 */
export default function SkillSelectionPanel({
  title,
  description,
  skills,
  selectedSkillIds,
  loading,
  onToggleSkill,
}: SkillSelectionPanelProps) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {description}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : skills.length === 0 ? (
        <Alert severity="info">No skills available.</Alert>
      ) : (
        <Box
          sx={{
            maxHeight: 220,
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <FormGroup>
            {skills.map((skill) => (
              <FormControlLabel
                key={skill.id}
                control={
                  <Checkbox
                    checked={selectedSkillIds.includes(skill.id)}
                    onChange={(event) => onToggleSkill(skill.id, event.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{skill.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {skill.category}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Selected {selectedSkillIds.length} / {skills.length}
      </Typography>
    </Box>
  );
}

